import type { StationKind, VehicleType } from '@/state/types';

/**
 * Phase 12.2 — Asset manifest.
 *
 * Maps known asset filenames in `public/vehicles/`, `public/stations/` and
 * `public/sounds/` to game entities. `null` means "no real asset, fall back
 * to the silhouette / synthetic-WebAudio path".
 *
 * The manifest is hand-curated so missing assets fail noisily at edit time
 * instead of silently 404-ing in production.
 */

export const VEHICLE_IMAGES: Record<VehicleType, string | null> = {
  // Fire
  ELW: '/vehicles/ELW.png',
  MTW: '/vehicles/MTW.png',
  DLK: '/vehicles/DLK.png',
  PKW: '/vehicles/PKW.png',
  TLF: '/vehicles/TLF.png',
  HLF: '/vehicles/HLF.png',
  LF: '/vehicles/LF.png',
  GW: '/vehicles/GW.png',
  RW: '/vehicles/RW.png',
  SW: '/vehicles/SW.png',
  TSF: '/vehicles/TSF.png',
  FWK: '/vehicles/FWK.png',
  // Phase 13.5 — GW-Varianten ohne eigene Bilder, fallback auf Silhouette.
  GWL: null,
  GWG: null,
  // EMS
  KTW: '/vehicles/KTW.png',
  RTW: '/vehicles/RTW.png',
  NEF: '/vehicles/NEF.png',
  // Police — no real assets shipped yet, silhouette fallback.
  FuStW: null,
  GruKW: null,
  KradPol: null,
  PolPKW: null,
  GefKW: null,
  WaWe: null,
  SEKWg: null,
  MEKWg: null,
  PolHelo: null,
};

export const STATION_IMAGES: Record<StationKind, string | null> = {
  'bf-station': '/stations/bf-station.png',
  'ff-station': '/stations/ff-station.png',
  'ems-station': '/stations/ems-station.png',
  hospital: '/stations/hospital.png',
  // Police kinds + courthouse — no real assets shipped yet.
  'police-station': null,
  'riot-police-station': null,
  'sek-base': null,
  'federal-police-station': null,
  courthouse: null,
};

/* ──────────── Sound mapping ──────────── */

/**
 * All sound events the game can fire. Each maps to either a file under
 * `public/sounds/` or `null` (synthetic WebAudio fallback in `lib/audio.ts`).
 */
export type SoundEvent =
  | 'mission-spawn'
  | 'siren-burst'
  | 'arrival'
  | 'radio-bleep'
  | 'arrest'
  | 'success'
  | 'failure'
  | 'escalation'
  | 'level-up'
  | 'upkeep-tick';

export interface SoundDef {
  /** Path under /public, or null for the synthetic fallback. */
  file: string | null;
  /** Default volume 0..1; multiplied by the user's master volume. */
  volume: number;
}

export const SOUND_MAP: Record<SoundEvent, SoundDef> = {
  // Phase 12.2.4 — Real assets present:
  'mission-spawn': { file: '/sounds/newMission.mp3', volume: 0.6 },
  'success': { file: '/sounds/money.mp3', volume: 0.5 },
  // Synthetic fallbacks for the rest.
  'siren-burst': { file: null, volume: 0.5 },
  'arrival': { file: null, volume: 0.4 },
  'radio-bleep': { file: null, volume: 0.4 },
  'arrest': { file: null, volume: 0.5 },
  'failure': { file: null, volume: 0.5 },
  'escalation': { file: null, volume: 0.5 },
  'level-up': { file: null, volume: 0.6 },
  'upkeep-tick': { file: null, volume: 0.3 },
};

/** All sound files referenced in SOUND_MAP — useful for preloading. */
export const SOUND_FILES_TO_PRELOAD: string[] = Object.values(SOUND_MAP)
  .map((s) => s.file)
  .filter((f): f is string => f !== null);
